/**
 * How long the loading overlay stays: shown while a load runs and for at
 * least `holdMs` from when it appeared (a quick switch doesn't flash it),
 * then "leaving" for `fadeMs` while it fades out, then hidden. A new load
 * brings it straight back.
 *
 * Timers mark the hold and the fade as done; what's shown follows from those
 * and whether a load is running, so rendering reads no clock.
 */
import { useEffect, useState } from "react";

export type HeldVisibility = "shown" | "leaving" | "hidden";

export function useHeldVisibility(active: boolean, holdMs: number, fadeMs: number): HeldVisibility {
  const [wasActive, setWasActive] = useState(active);
  // Nothing to hold or fade until a load has shown the overlay.
  const [holdDone, setHoldDone] = useState(!active);
  const [fadeDone, setFadeDone] = useState(!active);

  // Adjusting state while rendering, React's pattern for "follow a prop": a
  // load starting shows the overlay (again), with a fresh hold and fade.
  if (active !== wasActive) {
    setWasActive(active);
    if (active) {
      setHoldDone(false);
      setFadeDone(false);
    }
  }

  // The hold runs from when the overlay appears, however long the load takes.
  useEffect(() => {
    if (holdDone) return;
    const timer = setTimeout(() => {
      setHoldDone(true);
    }, holdMs);
    return () => {
      clearTimeout(timer);
    };
  }, [holdDone, holdMs]);

  // The fade starts once the load is over and the hold has passed.
  const leaving = !active && holdDone && !fadeDone;
  useEffect(() => {
    if (!leaving) return;
    const timer = setTimeout(() => {
      setFadeDone(true);
    }, fadeMs);
    return () => {
      clearTimeout(timer);
    };
  }, [leaving, fadeMs]);

  if (active || !holdDone) return "shown";
  return leaving ? "leaving" : "hidden";
}
